import { useEffect, useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { FieldErrors, FormProvider, useForm } from 'react-hook-form';
import Divider from '../common/Divider';
import {
    ResetPasswordFormInput,
    ResetPasswordFormValues,
    resetPasswordSchema
} from '../form/schema/resetPasswordSchema';

import { resetUserPassword } from '@/services/UserService';
import { useAppSelector } from '@/state/hooks';
import { selectCredentials } from '@/state/reducers/authSlice';
import useLanguage from '@/utils/hooks/useLanguage';
import useToast from '@/utils/hooks/useToast';
import { getPasswordValidations } from '@/utils/validators';
import cn from 'classnames';
import zxcvbn from 'zxcvbn';
import { Button } from '../common/Button';
import PasswordStrengthBar from '../common/PasswordStrengthBar';
import Input from '../inputs/Input';
import InputController from '../inputs/InputController';
import Modal, { ModalClose, ModalHeader, ModalProps, ModalTitle } from './Modal';

const defaultValues: Partial<ResetPasswordFormInput> = {
    current_password: undefined,
    new_password: undefined,
    confirm_new_password: undefined
};

const ResetPasswordModal = (props: ModalProps) => {
    const { opened, handlers } = props;

    const lang = useLanguage();
    const [toastError, toastSuccess] = useToast();

    const credentials = useAppSelector(selectCredentials);

    const form = useForm<ResetPasswordFormInput>({
        defaultValues,
        mode: 'onChange',
        resolver: yupResolver(resetPasswordSchema)
    });

    const {
        watch,
        reset,
        trigger,
        setError,
        handleSubmit,
        formState: { isSubmitting, isValid }
    } = form;

    const onSubmit = async (data: ResetPasswordFormInput) => {
        const dataValidated = data as ResetPasswordFormValues;
        const { current_password, new_password, confirm_new_password } = dataValidated;
        if (credentials) {
            if (new_password === confirm_new_password) {
                const resp = await resetUserPassword(credentials, current_password, new_password);

                if (resp.succeeded) {
                    const title = resp.message[0];
                    const body = 'Your password had been successfully changed to the new password.';
                    toastSuccess({ body, title });
                    handlers.close();
                } else {
                    const title = 'Change User Password';
                    const body = resp.message[0];
                    toastError({ body, title, persist: true });
                }
            } else {
                const title = 'Change User Password';
                const body = 'Confirm Password does not match with the New Password.';
                setError('confirm_new_password', { type: 'custom', message: body });
                toastError({ body, title, persist: true });
            }
        }
    };

    const [new_password, confirm_new_password] = watch(['new_password', 'confirm_new_password']);

    useEffect(() => {
        if (confirm_new_password) trigger('confirm_new_password');
    }, [new_password, confirm_new_password]);

    const passwordStrength = useMemo(() => {
        if (!new_password) return 0;
        return zxcvbn(new_password).score;
    }, [new_password]);

    const validation = getPasswordValidations([
        {
            name: 'password:min-length',
            description: 'Password minumum length',
            type: 'integer',
            value: '10'
        },
        {
            name: 'password:complexity',
            description: 'Password complexity score',
            type: 'integer',
            value: '2'
        }
    ]);

    const onError = (e: FieldErrors) => console.log(e);

    useEffect(() => {
        if (opened) {
            reset(defaultValues);
        }
    }, [opened]);

    return (
        <Modal {...props} className="h-auto" size="max-w-lg">
            <ModalHeader>
                <ModalTitle>{lang.settings.changePassword}</ModalTitle>
                <ModalClose handlers={handlers} />
            </ModalHeader>
            <Divider />
            <FormProvider {...form}>
                <form onSubmit={handleSubmit(onSubmit, onError)}>
                    <div className="p-2 sm:p-4">
                        <div className="flex flex-col gap-4">
                            <div className="space-y-3 sm:space-y-2">
                                <InputController
                                    name="current_password"
                                    label="Current Password"
                                    placeholder="••••••••"
                                    required>
                                    <Input type="password" />
                                </InputController>
                                <div className={cn('', { 'space-y-3 sm:space-y-2': new_password })}>
                                    <InputController
                                        name="new_password"
                                        label="New Password"
                                        placeholder="••••••••"
                                        required>
                                        <Input type="password" />
                                    </InputController>
                                    <PasswordStrengthBar
                                        password={new_password as string}
                                        currentComplexity={passwordStrength}
                                        passwordRequirement={validation}
                                    />
                                </div>

                                <InputController
                                    name="confirm_new_password"
                                    label="Confirm Password"
                                    placeholder="••••••••"
                                    required>
                                    <Input type="password" />
                                </InputController>
                            </div>
                        </div>
                    </div>
                    <Divider />
                    <div className="flex flex-row justify-between text-brand-text text-sm items-center p-2 sm:p-4">
                        <div className="justify-end flex space-x-2">
                            <button
                                type="button"
                                className="rounded-md p-2 px-4 bg-brand-neutral-700 hover:bg-brand-neutral-600"
                                onClick={handlers.close}>
                                {lang.common.cancel}
                            </button>
                            <Button
                                type="button"
                                className="rounded-md p-2 px-4 bg-brand-primary hover:bg-brand-primary-light"
                                onClick={handleSubmit(onSubmit, onError)}
                                isLoading={isSubmitting}
                                disabled={isSubmitting || !(passwordStrength >= 2) || !isValid}>
                                {lang.settings.changePassword}
                            </Button>
                        </div>
                    </div>
                </form>
            </FormProvider>
        </Modal>
    );
};

export default ResetPasswordModal;
